/**
 * AdminKvkkRequests.js — yöneticinin hesap kapatma / silme yüzeyi.
 *
 * ⚠️ 2026-08-31 DAVRANIŞ DEĞİŞİKLİĞİ — bu ekran neden ikiye ayrıldı:
 * Öğrencinin "Hesabımı Sil" akışı artık veri silmiyor, hesabı ANINDA pasife
 * alıyor (`users.account_status = "deactivated"`, bkz. backend
 * services/account_deactivation.py). Bu yol `is_deletion_pending` bayrağına
 * HİÇ DOKUNMUYOR. Ekran eskiden yalnızca o bayrağa bakıyordu; yani öğrenci
 * hesabını kapattığında burada HİÇBİR ŞEY görünmüyordu ve yöneticinin pasif
 * hesapları görebileceği tek yüzey sessizce boşalmıştı.
 *
 * Artık İKİ AYRI küme gösteriliyor — bunlar farklı şeylerdir, tek listede
 * karıştırılmaz:
 *
 *   1) PASİF HESAPLAR — `account_status === 'deactivated'`.
 *      Öğrencinin kendi kapattığı (veya yöneticinin kapattığı) hesap.
 *      Hiçbir veri silinmedi; hesap yalnızca girişe kapalı.
 *      Aksiyon: YENİDEN AKTİFLEŞTİR.
 *
 *   2) SİLİNMEYE PROGRAMLI — `is_deletion_pending === true`.
 *      YALNIZCA yöneticinin programlayabildiği GERÇEK silme (hard purge).
 *      backend scheduler.purge_expired_deletions grace bitince gerçekten siler.
 *      Aksiyon: SİLME TALEBİNİ İPTAL ET.
 *
 * İKİ KÜMENİN KESİŞİMİ (bilinçli karar): filtreler bağımsız olduğu için pasif
 * BİR hesap ayrıca silinmeye programlanırsa satır İKİ bölümde birden görünür.
 * Satırı pasif listeden ÇIKARMAK yanlış olurdu: yeniden aktifleştirme
 * butonu yalnızca o bölümde ve o hesap tam da yanlışlıkla kapanmış olabilecek
 * hesap — dışlamak, düzeltmenin tek yüzeyini kapatır ve bu ekranın emniyet
 * sübabını yeniden kırardı. Bunun yerine her iki satıra da KARŞI DURUMU
 * anlatan bir rozet konur ("Ayrıca silinmeye programlı" / "Hesap zaten
 * pasif"), böylece yönetici iki aksiyonu da görerek seçer.
 *
 * Backend uçları (backend kodundan okunarak doğrulandı):
 *   - Liste : GET /api/admin/users  (routes/admin.py::admin_list_users —
 *     projeksiyon `{_id:0, password_hash:0}`, yani account_status /
 *     deactivated_at / deactivation_reason alanları OLDUĞU GİBİ döner).
 *   - Yeniden aktifleştir : PUT /api/admin/students/{user_id}/profile
 *     body { account_status: "active", account_status_reason: "<serbest metin>" }
 *     → backend deactivated_at ve deactivation_reason'ı temizler,
 *     reactivated_at yazar, audit log'lar.
 *   - Silme talebini iptal : PUT /api/admin/students/{user_id}/profile
 *     body { is_deletion_pending: false, deletion_scheduled_at: null,
 *     deletion_grace_until: null }  (mevcut davranış korundu — gerekçe
 *     İSTEMEZ, o gövdeye account_status_reason EKLENMEZ).
 *
 * ⚠️ ZORUNLU GEREKÇE (backend/routes/admin.py::admin_update_student_profile,
 * "account_status için ÜÇ SERTLEŞTİRME" bloğu — okunarak doğrulandı):
 * gövdede `account_status` varsa `account_status_reason` ZORUNLUDUR.
 * Backend `str(body.get("account_status_reason") or "").strip()` yapar; boş
 * VEYA yalnız boşluk ise 400 `account_status_reason_required` döner ve
 * HİÇBİR yazma yapmaz. `allowed` allowlist'inde DEĞİL, ayrı okunur — profil
 * alanı değil, işlem gerekçesidir. Bu yüzden modalda zorunlu bir metin
 * girdisi var; boşken onay butonu devre dışı ve istek HİÇ gönderilmez.
 *
 * SAVUNMACI OKUMA: alanların hiçbiri garanti değil. `account_status`
 * gelmezse (eski kayıt / projeksiyon değişirse) karşılaştırma sessizce false
 * döner, ekran patlamaz — undefined'a tarih formatlaması yapılmaz.
 */
import { useState, useEffect } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage, getApiErrorCode } from '../utils/apiError';
import { ShieldAlert, XCircle, RefreshCw, Clock, User, UserCheck, UserX, Trash2 } from 'lucide-react';
import PageHeader from '../components/admin/PageHeader';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_REACTIVATE = 'reactivate';
const MODE_CANCEL_DELETION = 'cancelDeletion';

function daysRemaining(isoDate) {
  if (!isoDate) return null;
  const diff = new Date(isoDate) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function formatDate(isoDate, lang) {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US');
}

export default function AdminKvkkRequests() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  const [deactivated, setDeactivated] = useState([]);
  const [pendingDeletion, setPendingDeletion] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyUserId, setBusyUserId] = useState(null);
  // { user, mode }
  const [confirmModal, setConfirmModal] = useState(null);
  // Yalnızca MODE_REACTIVATE için: backend'in zorunlu kıldığı işlem gerekçesi.
  const [statusReason, setStatusReason] = useState('');

  // Modal her açılışta TEMİZ gerekçeyle açılır — bir önceki kullanıcının
  // gerekçesinin başka bir hesabın audit kaydına düşmesi sessiz bir hatadır.
  const openConfirm = (user, mode) => {
    setStatusReason('');
    setConfirmModal({ user, mode });
  };

  const closeConfirm = () => {
    setStatusReason('');
    setConfirmModal(null);
  };

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: 0, limit: 200 });
      const res = await axios.get(`${API}/admin/users?${params}`, { headers: authHeaders() });
      const all = res.data?.users || [];
      setDeactivated(all.filter(u => u.account_status === 'deactivated'));
      setPendingDeletion(all.filter(u => u.is_deletion_pending === true));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAccounts(); }, []);

  const reactivateAccount = async (userId, rawReason) => {
    // Boş/boşluk gerekçe backend'de 400'e çarpar ve hiçbir şey yazılmaz.
    // Buton zaten devre dışı; bu ikinci kapı (Enter, otomasyon, gelecekteki
    // refactor) isteğin HİÇ gitmemesini garanti eder.
    const reason = (rawReason || '').trim();
    if (!reason) {
      toast.error(
        lang === 'tr'
          ? 'Hesabı yeniden açmak için gerekçe yazmalısınız.'
          : 'You must enter a reason to reopen the account.'
      );
      return;
    }
    setBusyUserId(userId);
    try {
      await axios.put(
        `${API}/admin/students/${userId}/profile`,
        { account_status: 'active', account_status_reason: reason },
        { headers: authHeaders() }
      );
      toast.success(lang === 'tr' ? 'Hesap yeniden aktifleştirildi' : 'Account reactivated');
      closeConfirm();
      fetchAccounts();
    } catch (err) {
      // Gerekçe reddi özel olarak adlandırılır: jenerik "Request failed with
      // status code 400" yöneticiye ne yapacağını söylemez.
      if (getApiErrorCode(err) === 'account_status_reason_required') {
        toast.error(
          lang === 'tr'
            ? 'Gerekçe boş olamaz — hesap durumu değiştirilmedi.'
            : 'The reason cannot be empty — the account status was not changed.'
        );
      } else {
        toast.error(getApiErrorMessage(err, lang));
      }
    } finally {
      setBusyUserId(null);
    }
  };

  const cancelDeletion = async (userId) => {
    setBusyUserId(userId);
    try {
      await axios.put(
        `${API}/admin/students/${userId}/profile`,
        {
          is_deletion_pending: false,
          deletion_scheduled_at: null,
          deletion_grace_until: null,
        },
        { headers: authHeaders() }
      );
      toast.success(
        lang === 'tr' ? 'Silme talebi iptal edildi' : 'Deletion request cancelled'
      );
      closeConfirm();
      fetchAccounts();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setBusyUserId(null);
    }
  };

  const reasonLabel = (reason) => {
    if (reason === 'user_request') {
      return lang === 'tr' ? 'Öğrenci kapattı' : 'Closed by student';
    }
    if (reason === 'admin') {
      return lang === 'tr' ? 'Yönetici kapattı' : 'Closed by admin';
    }
    return lang === 'tr' ? 'Kaynak belirtilmemiş' : 'Source not recorded';
  };

  const confirmCopy = () => {
    if (!confirmModal) return null;
    const who = confirmModal.user.name || confirmModal.user.email;
    if (confirmModal.mode === MODE_REACTIVATE) {
      return {
        title: lang === 'tr' ? 'Hesabı Yeniden Aktifleştir' : 'Reactivate Account',
        body: lang === 'tr'
          ? `${who} kullanıcısının hesabını yeniden açmak istediğinize emin misiniz? Hesap tekrar giriş yapabilir duruma gelir. Kullanıcı bilgilendirilmeyecek.`
          : `Are you sure you want to reopen the account for ${who}? The account will be able to sign in again. The user will not be notified.`,
        confirm: lang === 'tr' ? 'Yeniden Aktifleştir' : 'Reactivate',
        icon: UserCheck,
        // Backend bu aksiyonda gerekçeyi ZORUNLU kılar (bkz. dosya başlığı).
        needsReason: true,
      };
    }
    return {
      title: lang === 'tr' ? 'Silme Talebini İptal Et' : 'Cancel Deletion Request',
      body: lang === 'tr'
        ? `${who} kullanıcısının hesap silme talebini iptal etmek istediğinize emin misiniz? Kullanıcı bilgilendirilmeyecek.`
        : `Are you sure you want to cancel the deletion request for ${who}? The user will not be notified.`,
      confirm: lang === 'tr' ? 'Silmeyi İptal Et' : 'Cancel Deletion',
      icon: XCircle,
      // Silme iptali gövdesi gerekçe İSTEMEZ — birebir korunur.
      needsReason: false,
    };
  };

  const copy = confirmCopy();
  const ConfirmIcon = copy?.icon || XCircle;
  const reasonMissing = !!copy?.needsReason && statusReason.trim().length === 0;
  const confirmDisabled =
    !!confirmModal && (busyUserId === confirmModal.user.user_id || reasonMissing);

  return (
    <AdminLayout>
      <div data-testid="admin-kvkk-page">
        <PageHeader
          breadcrumbs={[
            { label: 'Admin', href: '/admin' },
            { label: lang === 'tr' ? 'Belgeler & Hukuk' : 'Documents & Legal' },
            { label: lang === 'tr' ? 'KVKK Talepleri' : 'KVKK Requests' },
          ]}
          title={lang === 'tr' ? 'Pasif Hesaplar ve Silme Talepleri' : 'Deactivated Accounts & Deletion Requests'}
          subtitle={lang === 'tr'
            ? 'Kendi hesabını kapatan öğrenciler ve silinmeye programlanmış hesaplar'
            : 'Students who closed their own account, and accounts scheduled for deletion'}
          actions={
            <button
              onClick={fetchAccounts}
              disabled={loading}
              className="p-2 rounded-xl border-2 border-[var(--border-light)] hover:bg-gray-50 disabled:opacity-40"
              data-testid="kvkk-refresh-btn"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          }
        />

        {/* İki kümenin farkı — yöneticinin yanlış aksiyon almasını önler */}
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-6 text-sm" data-testid="kvkk-info-box">
          <p className="font-bold text-amber-700 mb-1">
            {lang === 'tr' ? 'Bu iki liste farklı şeylerdir' : 'These two lists are different things'}
          </p>
          <p className="text-amber-600 text-xs">
            {lang === 'tr'
              ? 'Pasif hesaplarda hiçbir veri silinmemiştir; hesap yalnızca girişe kapalıdır ve yeniden açılabilir. Silinmeye programlı hesaplar ise bekleme süresi dolunca kalıcı olarak silinir — bu yalnızca yöneticinin başlatabildiği bir işlemdir.'
              : 'Deactivated accounts have lost no data; they are only locked out of sign-in and can be reopened. Accounts scheduled for deletion are permanently removed once the grace period expires — only an administrator can start that.'}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* ---------- 1) PASİF HESAPLAR ---------- */}
            <section data-testid="kvkk-deactivated-section">
              <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
                <UserX size={18} className="text-slate-500" />
                {lang === 'tr' ? 'Pasif Hesaplar' : 'Deactivated Accounts'}
              </h2>

              {deactivated.length === 0 ? (
                <div className="card-clay p-10 text-center" data-testid="kvkk-deactivated-empty">
                  <ShieldAlert size={36} className="mx-auto text-green-400 mb-3" />
                  <p className="font-bold text-base text-[var(--text-main)]">
                    {lang === 'tr' ? 'Pasif hesap yok' : 'No deactivated accounts'}
                  </p>
                  <p className="text-sm text-[var(--text-muted)] mt-1">
                    {lang === 'tr'
                      ? 'Bir öğrenci hesabını kapattığında burada görünür ve buradan yeniden açılabilir.'
                      : 'When a student closes their account it appears here, and can be reopened from here.'}
                  </p>
                </div>
              ) : (
                <div className="card-clay overflow-hidden">
                  <div className="p-4 border-b-2 border-[var(--border-light)] flex items-center gap-2">
                    <span className="px-2.5 py-1 bg-slate-100 text-slate-700 text-xs font-bold rounded-lg">
                      {deactivated.length} {lang === 'tr' ? 'pasif hesap' : 'deactivated'}
                    </span>
                  </div>
                  <div className="divide-y divide-[var(--border-light)]">
                    {deactivated.map((u, i) => {
                      const closedAt = formatDate(u.deactivated_at, lang);
                      return (
                        <motion.div
                          key={u.user_id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.04 }}
                          className="p-4 flex items-center gap-4"
                          data-testid={`kvkk-deactivated-row-${u.user_id}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <UserX size={18} className="text-slate-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm truncate">{u.name || u.email}</p>
                            <p className="text-xs text-[var(--text-muted)] truncate">{u.email}</p>
                            <div className="flex flex-wrap gap-3 mt-1 text-[10px] text-[var(--text-muted)]">
                              <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-bold">
                                {reasonLabel(u.deactivation_reason)}
                              </span>
                              {/* Çakışma rozeti — aynı satır alt bölümde de var */}
                              {u.is_deletion_pending === true && (
                                <span
                                  className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-bold"
                                  data-testid={`kvkk-also-pending-badge-${u.user_id}`}
                                >
                                  {lang === 'tr'
                                    ? 'Ayrıca silinmeye programlı'
                                    : 'Also scheduled for deletion'}
                                </span>
                              )}
                              {closedAt && (
                                <span>
                                  {lang === 'tr' ? 'Kapatıldı:' : 'Closed:'} {closedAt}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <button
                              onClick={() => openConfirm(u, MODE_REACTIVATE)}
                              disabled={busyUserId === u.user_id}
                              className="px-3 py-1.5 rounded-xl text-xs font-bold border-2 border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 flex items-center gap-1.5 disabled:opacity-40"
                              data-testid={`kvkk-reactivate-btn-${u.user_id}`}
                            >
                              <UserCheck size={12} />
                              {lang === 'tr' ? 'Yeniden Aktifleştir' : 'Reactivate'}
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>

            {/* ---------- 2) SİLİNMEYE PROGRAMLI ---------- */}
            <section data-testid="kvkk-pending-section">
              <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
                <Trash2 size={18} className="text-rose-500" />
                {lang === 'tr' ? 'Silinmeye Programlı Hesaplar' : 'Accounts Scheduled for Deletion'}
              </h2>

              {pendingDeletion.length === 0 ? (
                <div className="card-clay p-10 text-center" data-testid="kvkk-pending-empty">
                  <ShieldAlert size={36} className="mx-auto text-green-400 mb-3" />
                  <p className="font-bold text-base text-[var(--text-main)]">
                    {lang === 'tr' ? 'Silinmeye programlı hesap yok' : 'No accounts scheduled for deletion'}
                  </p>
                  <p className="text-sm text-[var(--text-muted)] mt-1">
                    {lang === 'tr'
                      ? 'Bir hesabı kalıcı silmeye yalnızca yönetici programlayabilir; programlanan hesaplar burada listelenir.'
                      : 'Only an administrator can schedule a permanent deletion; scheduled accounts are listed here.'}
                  </p>
                </div>
              ) : (
                <div className="card-clay overflow-hidden">
                  <div className="p-4 border-b-2 border-[var(--border-light)] flex items-center gap-2">
                    <span className="px-2.5 py-1 bg-rose-100 text-rose-700 text-xs font-bold rounded-lg">
                      {pendingDeletion.length} {lang === 'tr' ? 'bekleyen silme' : 'pending deletions'}
                    </span>
                  </div>
                  <div className="divide-y divide-[var(--border-light)]">
                    {pendingDeletion.map((u, i) => {
                      const days = daysRemaining(u.deletion_grace_until);
                      const isUrgent = days !== null && days <= 3;
                      const requestedAt = formatDate(u.deletion_scheduled_at, lang);
                      return (
                        <motion.div
                          key={u.user_id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.04 }}
                          className="p-4 flex items-center gap-4"
                          data-testid={`kvkk-row-${u.user_id}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                            <User size={18} className="text-rose-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm truncate">{u.name || u.email}</p>
                            <p className="text-xs text-[var(--text-muted)] truncate">{u.email}</p>
                            <div className="flex flex-wrap gap-3 mt-1 text-[10px] text-[var(--text-muted)]">
                              {/* Çakışma rozeti — aynı satır üst bölümde de var */}
                              {u.account_status === 'deactivated' && (
                                <span
                                  className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-bold"
                                  data-testid={`kvkk-also-deactivated-badge-${u.user_id}`}
                                >
                                  {lang === 'tr'
                                    ? 'Hesap zaten pasif'
                                    : 'Account already deactivated'}
                                </span>
                              )}
                              {requestedAt && (
                                <span>
                                  {lang === 'tr' ? 'Programlandı:' : 'Scheduled:'} {requestedAt}
                                </span>
                              )}
                              {u.deletion_grace_until && (
                                <span className={`flex items-center gap-1 font-bold ${isUrgent ? 'text-rose-600' : 'text-orange-500'}`}>
                                  <Clock size={10} />
                                  {days !== null
                                    ? days > 0
                                      ? `${days} ${lang === 'tr' ? 'gün kaldı' : 'days left'}`
                                      : (lang === 'tr' ? 'Süresi doldu' : 'Grace expired')
                                    : '-'}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <button
                              onClick={() => openConfirm(u, MODE_CANCEL_DELETION)}
                              disabled={busyUserId === u.user_id}
                              className="px-3 py-1.5 rounded-xl text-xs font-bold border-2 border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1.5 disabled:opacity-40"
                              data-testid={`kvkk-cancel-btn-${u.user_id}`}
                            >
                              <XCircle size={12} />
                              {lang === 'tr' ? 'İptal Et' : 'Cancel Deletion'}
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        {/* Onay modalı — iki aksiyon da buradan geçer */}
        <AnimatePresence>
          {confirmModal && copy && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => !busyUserId && closeConfirm()}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
                onClick={e => e.stopPropagation()}
                data-testid="kvkk-confirm-modal"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <ConfirmIcon size={20} className="text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold">{copy.title}</h3>
                    <p className="text-xs text-[var(--text-muted)]">{confirmModal.user.email}</p>
                  </div>
                </div>
                <p className="text-sm text-[var(--text-muted)] mb-5">{copy.body}</p>

                {/* Zorunlu işlem gerekçesi — YALNIZCA hesap durumu değişiminde.
                    Yönetici tarafına görünür, öğrenciye gösterilmez; audit
                    kaydına ve users dokümanına yazılır. */}
                {copy.needsReason && (
                  <div className="mb-5">
                    <label
                      htmlFor="kvkk-status-reason"
                      className="block text-xs font-bold text-[var(--text-main)] mb-1"
                    >
                      {lang === 'tr' ? 'Gerekçe' : 'Reason'}{' '}
                      <span className="text-rose-600 font-bold">
                        ({lang === 'tr' ? 'zorunlu' : 'required'})
                      </span>
                    </label>
                    <textarea
                      id="kvkk-status-reason"
                      rows={3}
                      required
                      aria-required="true"
                      aria-describedby="kvkk-status-reason-hint"
                      value={statusReason}
                      onChange={(e) => setStatusReason(e.target.value)}
                      disabled={!!busyUserId}
                      placeholder={lang === 'tr'
                        ? 'Örn. Öğrenci destek talebiyle hesabının geri açılmasını istedi (talep #1234).'
                        : 'e.g. The student asked for their account to be reopened via support (ticket #1234).'}
                      className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] text-sm focus:outline-none focus:border-sky-400 disabled:opacity-40"
                      data-testid="kvkk-status-reason-input"
                    />
                    <p
                      id="kvkk-status-reason-hint"
                      className="text-[11px] text-[var(--text-muted)] mt-1"
                    >
                      {lang === 'tr'
                        ? 'Gerekçe zorunludur ve denetim kaydına yazılır; boş bırakılırsa işlem yapılamaz. Öğrenciye gösterilmez.'
                        : 'A reason is required and is written to the audit log; the action cannot proceed while it is empty. It is not shown to the student.'}
                    </p>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={closeConfirm}
                    disabled={!!busyUserId}
                    className="flex-1 px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-sm font-bold hover:bg-gray-50 disabled:opacity-40"
                  >
                    {lang === 'tr' ? 'Vazgeç' : 'Back'}
                  </button>
                  <button
                    onClick={() => (
                      confirmModal.mode === MODE_REACTIVATE
                        ? reactivateAccount(confirmModal.user.user_id, statusReason)
                        : cancelDeletion(confirmModal.user.user_id)
                    )}
                    disabled={confirmDisabled}
                    aria-disabled={confirmDisabled ? 'true' : undefined}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 disabled:opacity-40 flex items-center justify-center gap-2"
                    data-testid="kvkk-confirm-cancel-btn"
                  >
                    <ConfirmIcon size={14} />
                    {busyUserId === confirmModal.user.user_id ? '...' : copy.confirm}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Kalıcı silme — bu arayüzden yapılamaz */}
        <div className="mt-6 bg-gray-50 border-2 border-[var(--border-light)] rounded-xl p-4 text-xs text-[var(--text-muted)]">
          <p className="font-bold mb-1">
            {lang === 'tr' ? 'Kalıcı Silme' : 'Permanent Deletion'}
          </p>
          <p>
            {lang === 'tr'
              ? 'Bir hesabı kalıcı olarak silmek (KVKK md. 7 anlamında gerçek silme) bu arayüzden yapılamaz. Pasif bir hesabı silmek ya da bekleme süresi dolmadan silmek için sistem yöneticisiyle iletişime geçin.'
              : 'Permanently deleting an account (a real erasure under KVKK Art. 7) is not available from this interface. To delete a deactivated account, or to delete before the grace period expires, contact a system administrator.'}
          </p>
        </div>
      </div>
    </AdminLayout>
  );
}
